import React, { useState, useEffect, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from "recharts";
import apiClient from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../utils/permissions";

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(v) {
  if (v == null) return "—";
  const abs = Math.abs(v);
  let s;
  if (abs >= 1_000_000_000)     s = `${(abs / 1_000_000_000).toFixed(2)}B`;
  else if (abs >= 1_000_000)    s = `${(abs / 1_000_000).toFixed(2)}M`;
  else if (abs >= 1_000)        s = `${(abs / 1_000).toFixed(1)}K`;
  else                           s = abs.toLocaleString("he-IL", { maximumFractionDigits: 0 });
  return `${v < 0 ? "-" : ""}₪${s}`;
}

function fmtFull(v) {
  if (v == null) return "—";
  return new Intl.NumberFormat("he-IL", {
    style: "currency", currency: "ILS", maximumFractionDigits: 0,
  }).format(v);
}

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString("he-IL") : "—";
}

const now = new Date();
const Y   = now.getFullYear();

function buildDateRange(period) {
  switch (period) {
    case "annual": return {
      startDate: `${Y}-01-01`,
      endDate:   `${Y}-12-31`,
    };
    case "q1": return { startDate: `${Y}-01-01`, endDate: `${Y}-03-31` };
    case "q2": return { startDate: `${Y}-04-01`, endDate: `${Y}-06-30` };
    case "q3": return { startDate: `${Y}-07-01`, endDate: `${Y}-09-30` };
    case "q4": return { startDate: `${Y}-10-01`, endDate: `${Y}-12-31` };
    default:   return { startDate: `${Y}-01-01`, endDate: `${Y}-12-31` };
  }
}

const PERIOD_TABS = [
  { value: "annual", label: `שנתי ${Y}` },
  { value: "q1",     label: "Q1" },
  { value: "q2",     label: "Q2" },
  { value: "q3",     label: "Q3" },
  { value: "q4",     label: "Q4" },
];

const CATEGORY_LABELS = {
  PROJECT_REVENUE:   "הכנסות פרויקט",
  INVESTMENT_RETURN: "תשואת השקעה",
  INTEREST_RECEIVED: "ריבית שהתקבלה",
  OTHER_INCOME:      "הכנסה אחרת",
  DEBT_SERVICE:      "שירות חוב",
  OPERATING_EXPENSE: "הוצאה תפעולית",
  OTHER_EXPENSE:     "הוצאה אחרת",
};

// ── Recharts custom tooltip ───────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "var(--card-bg, #fff)",
      border: "1px solid var(--border, #e5e7eb)",
      borderRadius: 8,
      padding: "10px 14px",
      fontSize: 12,
      lineHeight: 1.7,
      direction: "rtl",
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4, color: "var(--text, #111)" }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {fmt(p.value)}
        </div>
      ))}
    </div>
  );
}

// ── Section line component (for P&L rows) ────────────────────────────────────

function PLRow({ label, amount, indent = 0, muted = false, bold = false, source }) {
  const style = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `5px ${12 + indent * 16}px`,
    fontSize: bold ? 13 : 12,
    fontWeight: bold ? 700 : 400,
    color: muted
      ? "var(--text-muted, #6b7280)"
      : bold
        ? "var(--text, #111)"
        : "var(--text-secondary, #374151)",
    borderBottom: bold ? "1px solid var(--border, #e5e7eb)" : "none",
    background: bold ? "var(--surface, #f9fafb)" : "transparent",
  };
  return (
    <div style={style}>
      <span>
        {label}
        {source && (
          <span style={{ marginRight: 6, fontSize: 10, color: "var(--text-muted)", fontWeight: 400 }}>
            ({source})
          </span>
        )}
      </span>
      <span style={{
        fontVariantNumeric: "tabular-nums",
        color: amount < 0 ? "var(--danger, #dc2626)" : bold ? "inherit" : "var(--text-secondary, #374151)",
      }}>
        {amount > 0 && !bold ? `+${fmt(amount)}` : fmt(amount)}
      </span>
    </div>
  );
}

function PLDivider({ label }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 12px 6px",
      fontSize: 11,
      fontWeight: 600,
      color: "var(--text-muted, #6b7280)",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      borderTop: "2px solid var(--border, #e5e7eb)",
      marginTop: 4,
    }}>
      {label}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function IncomeStatementPage({ onUnauthorized }) {
  const user = useCurrentUser();
  const canViewIsKpis    = can(user, 'incomeStatement.viewKpis');
  const canViewRevenue   = can(user, 'incomeStatement.viewRevenue');
  const canViewExpense   = can(user, 'incomeStatement.viewExpense');
  const canViewIsChart   = can(user, 'incomeStatement.viewChart');
  const [period, setPeriod]     = useState("annual");
  const [data,   setData]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error,   setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const range = buildDateRange(period);
      const res   = await apiClient.get(`/income-statement`, { params: range });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401 && onUnauthorized) onUnauthorized();
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [period, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  // ── Render states ─────────────────────────────────────────────────────────

  if (loading && !data) return (
    <div className="page-container" style={{ direction: "rtl" }}>
      <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>טוען דוח רווח והפסד...</div>
    </div>
  );

  if (error) return (
    <div className="page-container" style={{ direction: "rtl" }}>
      <div style={{ padding: 40, color: "var(--danger)" }}>שגיאה: {error}</div>
    </div>
  );

  const rev  = data?.revenue  || {};
  const exp  = data?.expenses || {};
  const monthly = data?.monthly || [];
  const netIncome  = data?.netIncome  ?? 0;
  const marginPct  = data?.marginPct  ?? 0;
  const totalRev   = rev.total  ?? 0;
  const totalExp   = exp.total  ?? 0;
  const profitable = netIncome >= 0;

  return (
    <div className="page-container" style={{ direction: "rtl" }}>

      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>דוח רווח והפסד</h1>
          {data?.period && (
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--text-muted)" }}>
              {fmtDate(data.period.start)} — {fmtDate(data.period.end)} ({data.period.days} ימים)
            </p>
          )}
        </div>

        {/* Period tabs */}
        <div style={{ display: "flex", gap: 4, background: "var(--surface, #f3f4f6)", borderRadius: 8, padding: 4 }}>
          {PERIOD_TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => setPeriod(t.value)}
              style={{
                padding: "5px 14px",
                borderRadius: 6,
                border: "none",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: period === t.value ? 700 : 400,
                background: period === t.value ? "var(--accent, #6366f1)" : "transparent",
                color:      period === t.value ? "#fff" : "var(--text-muted)",
                transition: "all 0.15s",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── KPI cards ────────────────────────────────────────────────────────── */}
      {canViewIsKpis && <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
        {[
          { label: "סך הכנסות",  value: totalRev,  color: "#2563eb", positive: true  },
          { label: "סך הוצאות",  value: totalExp,  color: "#dc2626", positive: false },
          {
            label: profitable ? "רווח נקי" : "הפסד נקי",
            value: netIncome,
            color: profitable ? "#059669" : "#dc2626",
            positive: profitable,
          },
          {
            label: "מרג' נקי",
            value: null,
            display: `${marginPct >= 0 ? "" : "-"}${Math.abs(marginPct).toFixed(1)}%`,
            color: marginPct >= 0 ? "#059669" : "#dc2626",
          },
        ].map((card, i) => (
          <div key={i} style={{
            background: "var(--card-bg, #fff)",
            border: "1px solid var(--border, #e5e7eb)",
            borderRadius: 10,
            padding: "16px 18px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          }}>
            <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
              {card.label}
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: card.color, fontVariantNumeric: "tabular-nums" }}>
              {card.display ?? fmt(card.value)}
            </div>
          </div>
        ))}
      </div>}

      {/* ── Main content: P&L + Chart ─────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 16, alignItems: "start" }}>

        {/* ── P&L Statement ──────────────────────────────────────────────────── */}
        <div style={{
          background: "var(--card-bg, #fff)",
          border: "1px solid var(--border, #e5e7eb)",
          borderRadius: 10,
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}>

          {/* Revenue section */}
          {canViewRevenue && <>
          <PLDivider label="הכנסות" />

          {/* Manual revenue */}
          {rev.manualRevenue?.length > 0 && rev.manualRevenue.map((e) => (
            <PLRow
              key={e._id}
              label={e.description}
              amount={e.amount}
              indent={1}
              source={CATEGORY_LABELS[e.category]}
            />
          ))}
          {rev.manualRevenue?.length === 0 && (
            <PLRow label="ללא הכנסות ידניות" amount={0} indent={1} muted />
          )}

          {/* Dividend income */}
          {rev.dividendLines?.length > 0 && (
            <>
              <div style={{ padding: "6px 12px 2px 12px", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", paddingRight: 24 }}>
                דיבידנדים
              </div>
              {rev.dividendLines.map((d, i) => (
                <PLRow key={i} label={d.label} amount={d.amount} indent={2} source="אוטומטי" />
              ))}
            </>
          )}

          {/* Generic asset cash flow */}
          {rev.assetCashFlowLines?.length > 0 && (
            <>
              <div style={{ padding: "6px 12px 2px 12px", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", paddingRight: 24 }}>
                תזרים נכסים
              </div>
              {rev.assetCashFlowLines.map((a, i) => (
                <PLRow key={i} label={a.label} amount={a.amount} indent={2} source="אוטומטי" />
              ))}
            </>
          )}

          {/* Total revenue */}
          <PLRow label="סך הכנסות" amount={totalRev} bold />
          </>}

          {/* Expenses section */}
          {canViewExpense && <>
          <PLDivider label="הוצאות" />

          {/* Manual expenses */}
          {exp.manualExpenses?.length > 0 && (
            <>
              <div style={{ padding: "6px 12px 2px 12px", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", paddingRight: 24 }}>
                הוצאות תפעוליות
              </div>
              {exp.manualExpenses.map((e) => (
                <PLRow
                  key={e._id}
                  label={e.description}
                  amount={-e.amount}
                  indent={2}
                  source={CATEGORY_LABELS[e.category]}
                />
              ))}
            </>
          )}

          {/* Interest expense on loans */}
          {exp.loanInterestLines?.length > 0 && (
            <>
              <div style={{ padding: "6px 12px 2px 12px", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", paddingRight: 24 }}>
                הוצאות ריבית על הלוואות
              </div>
              {exp.loanInterestLines.map((l, i) => (
                <PLRow key={i} label={l.label} amount={-l.amount} indent={2} source="אוטומטי" />
              ))}
            </>
          )}

          {(exp.manualExpenses?.length === 0 && exp.loanInterestLines?.length === 0) && (
            <PLRow label="ללא הוצאות" amount={0} indent={1} muted />
          )}

          {/* Total expenses */}
          <PLRow label="סך הוצאות" amount={-totalExp} bold />
          </>}

          {/* Net income */}
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "14px 12px",
            background: profitable ? "#f0fdf4" : "#fef2f2",
            borderTop: `2px solid ${profitable ? "#059669" : "#dc2626"}`,
          }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: profitable ? "#059669" : "#dc2626" }}>
              {profitable ? "רווח נקי" : "הפסד נקי"}
            </span>
            <span style={{ fontWeight: 800, fontSize: 16, color: profitable ? "#059669" : "#dc2626", fontVariantNumeric: "tabular-nums" }}>
              {fmtFull(netIncome)}
            </span>
          </div>
        </div>

        {/* ── Chart ──────────────────────────────────────────────────────────── */}
        {canViewIsChart && <div style={{
          background: "var(--card-bg, #fff)",
          border: "1px solid var(--border, #e5e7eb)",
          borderRadius: 10,
          padding: 20,
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 13, fontWeight: 700, color: "var(--text)" }}>
            הכנסות מול הוצאות — לפי חודש
          </h3>

          {monthly.length === 0 ? (
            <div style={{ textAlign: "center", color: "var(--text-muted)", padding: 40, fontSize: 13 }}>
              אין נתונים לתצוגה
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={monthly} barGap={2} margin={{ top: 4, right: 4, bottom: 4, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border, #e5e7eb)" vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 10, fill: "var(--text-muted, #6b7280)" }}
                  tickFormatter={(v) => {
                    const [yr, mo] = v.split("-");
                    return `${mo}/${yr.slice(2)}`;
                  }}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "var(--text-muted, #6b7280)" }}
                  tickFormatter={(v) => fmt(v)}
                  width={60}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend
                  iconType="rect"
                  iconSize={10}
                  formatter={(name) => <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>{name}</span>}
                />
                <ReferenceLine y={0} stroke="var(--border, #e5e7eb)" />
                <Bar dataKey="revenue"  name="הכנסות"  fill="#2563eb" radius={[3, 3, 0, 0]} maxBarSize={28} />
                <Bar dataKey="expenses" name="הוצאות"  fill="#ef4444" radius={[3, 3, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          )}

          {/* Breakdown summary */}
          <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[
              { label: "הכנסות ידניות",         value: rev.totalManualRevenue,  color: "#2563eb" },
              { label: "דיבידנדים",             value: rev.totalDividends,       color: "#2563eb" },
              { label: "תזרים נכסים",           value: rev.totalAssetCashFlow,   color: "#2563eb" },
              { label: "הוצאות תפעוליות",       value: exp.totalManualExpenses,  color: "#ef4444" },
              { label: "הוצאות ריבית להלוואות", value: exp.totalLoanInterest,    color: "#ef4444" },
            ].map((item, i) => (
              <div key={i} style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "5px 8px",
                background: "var(--surface, #f9fafb)",
                borderRadius: 6,
                fontSize: 11,
              }}>
                <span style={{ color: "var(--text-muted)" }}>{item.label}</span>
                <span style={{ fontWeight: 600, color: item.color, fontVariantNumeric: "tabular-nums" }}>
                  {fmt(item.value)}
                </span>
              </div>
            ))}
          </div>
        </div>}
      </div>
    </div>
  );
}
